import { useEffect, useMemo } from "react";
import { useCollectionData } from "react-firebase-hooks/firestore";

import { CHAT_TYPES } from "@constants/chat";
import { CONVERSATION_COLlECTION, MESSAGE_COLlECTION } from "@fb/const";
import { db } from "@fb/db";
import {
  DocumentData,
  collection,
  orderBy,
  query,
  where,
  WithFieldValue,
  QueryDocumentSnapshot,
  SnapshotOptions,
  FirestoreDataConverter,
} from "firebase/firestore";

import { AssistantMessageType, Conversation } from "@components/AssistantChat/type";

import { useMessageStore } from "@contexts/message.state";
import { useUserStore } from "@contexts/user.state";

const postConverter: FirestoreDataConverter<any> = {
  toFirestore(post: WithFieldValue<any>): DocumentData {
    return { author: post.author, title: post.title };
  },
  fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): any {
    const data = snapshot.data(options);
    return {
      id: snapshot.id,
      ...data,
    };
  },
};

export const useMessages = (conversationId: string) => {
  const q = useMemo(() => {
    if (!conversationId) {
      return null;
    }
    return query(
      collection(db, MESSAGE_COLlECTION).withConverter(postConverter),
      where("conversationId", "==", conversationId || ""),
      orderBy("date", "asc")
    );
  }, [conversationId]);

  return useCollectionData<AssistantMessageType>(q, {
    snapshotListenOptions: { includeMetadataChanges: true },
  });
};

export const useConversations = (
  userId: string | undefined,
  type: keyof typeof CHAT_TYPES
) => {
  const q = useMemo(() => {
    if (!userId || !type) {
      return null;
    }
    return query(
      collection(db, CONVERSATION_COLlECTION).withConverter(postConverter),
      where("participants", "array-contains-any", [userId]),
      where("type", "==", type)
      // orderBy("session", "asc") // Cannot sort by date because lacking of this field in some records
    );
  }, [userId, type]);

  return useCollectionData<Conversation>(q, {
    snapshotListenOptions: { includeMetadataChanges: true },
  });
};

export const useFetchMessages = (conversationId: string) => {
  const { messages, loading, fetchMessages, clearMessages } = useMessageStore();

  const userId = useUserStore((state) => state.user?.id);

  useEffect(() => {
    // Fetch messages when entering the page
    if (!conversationId || !userId) {
      return;
    }
    fetchMessages(conversationId);

    // Clear messages when leaving the page
    return () => {
      clearMessages();
    };
  }, [conversationId, fetchMessages, clearMessages, userId]);

  return { messages: messages[conversationId], loading, fetchMessages };
};
